import * as React from 'react';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';

import { useSignup } from '../context';
import type { LogoCandidate, LogoChoice, LogoState } from '../types';

// The confirm card's logo (plan §7 `logo/`): the invite's own logo search, started by `account`. It is
// polled while it runs; each candidate's image is fetched with the session header and shown from a
// blob: URL. Untouched, create sends `auto` (setup applies the search's pick); a click sends that
// candidate or `none`. The logo never blocks the sign-up.

const POLL_MS = 2000;
const MAX_POLLS = 60;
const TILE_W = 132;
const TILE_H = 80;

const checkerboard = {
    backgroundColor: '#fff',
    backgroundImage:
        'linear-gradient(45deg, #eee 25%, transparent 25%), linear-gradient(-45deg, #eee 25%, transparent 25%),'
        + ' linear-gradient(45deg, transparent 75%, #eee 75%), linear-gradient(-45deg, transparent 75%, #eee 75%)',
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
};

interface Props {
    value: LogoChoice;
    onChange: (value: LogoChoice) => void;
    disabled?: boolean;
    /** Test seams. */
    pollMs?: number;
    maxPolls?: number;
}

function selectedIndex(value: LogoChoice, logo: LogoState | null): number | null {
    if (value.choice === 'candidate') { return value.candidate; }
    if (value.choice === 'auto') { return logo ? logo.chosen : null; }
    return null;
}

export default function LogoPicker({ value, onChange, disabled = false, pollMs = POLL_MS, maxPolls = MAX_POLLS }: Props) {
    const { api, sessionLost } = useSignup();
    const [logo, setLogo] = React.useState<LogoState | null>(null);
    const [gaveUp, setGaveUp] = React.useState(false);
    const [images, setImages] = React.useState<Record<string, string>>({});
    const requested = React.useRef<Set<string>>(new Set());
    const urls = React.useRef<string[]>([]);

    React.useEffect(() => {
        let active = true;
        let timer: ReturnType<typeof setTimeout> | null = null;
        let polls = 0;
        const tick = async () => {
            try {
                const state = await api.logo();
                if (!active) { return; }
                setLogo(state);
                polls += 1;
                if (state.state === 'running') {
                    if (polls < maxPolls) {
                        timer = setTimeout(tick, pollMs);
                    } else {
                        setGaveUp(true);
                    }
                }
            } catch (e) {
                if (!active || sessionLost(e)) { return; }
                setLogo((prev) => prev || { state: 'failed', chosen: null, auto_apply: false, low_confidence: false, candidates: [] });
            }
        };
        tick();
        return () => {
            active = false;
            if (timer) { clearTimeout(timer); }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one search per card
    }, [api]);

    React.useEffect(() => {
        if (!logo || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') { return; }
        logo.candidates.forEach((c) => {
            if (requested.current.has(c.name)) { return; }
            requested.current.add(c.name);
            api.logoImage(c.name).then(
                (blob) => {
                    const url = URL.createObjectURL(blob);
                    urls.current.push(url);
                    setImages((prev) => ({ ...prev, [c.name]: url }));
                },
                (e) => { sessionLost(e); },
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logo, api]);

    React.useEffect(() => () => {
        if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
            urls.current.forEach((u) => URL.revokeObjectURL(u));
        }
    }, []);

    const searching = !logo || (logo.state === 'running' && !gaveUp);
    const candidates: LogoCandidate[] = logo ? logo.candidates : [];
    const selected = selectedIndex(value, logo);

    let caption: string;
    if (searching && candidates.length === 0) {
        caption = 'Looking for your logo on your website…';
    } else if (candidates.length === 0) {
        caption = 'We did not find a logo on your website. You can add one later in your dashboard settings.';
    } else if (value.choice === 'none') {
        caption = 'No logo for now. You can add one later in your dashboard settings.';
    } else if (value.choice === 'auto' && logo && logo.low_confidence) {
        caption = 'We are not sure this is your logo. Please check it, or choose another one.';
    } else if (value.choice === 'auto' && selected == null) {
        caption = 'Choose your logo, or "No logo" to add one later.';
    } else {
        caption = 'This logo will be used in your dashboard and your guests’ app.';
    }

    const tile = (active: boolean) => ({
        width: TILE_W,
        height: TILE_H,
        borderRadius: 1,
        border: 2,
        borderColor: active ? 'primary.main' : 'divider',
        overflow: 'hidden',
    });

    return (
        <Box role="group" aria-label="Logo">
            <Typography variant="subtitle2" gutterBottom>Logo</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                {candidates.map((c, i) => {
                    const active = selected === c.index;
                    return (
                        <ButtonBase
                            key={c.name}
                            type="button"
                            aria-pressed={active}
                            aria-label={`Logo option ${i + 1}`}
                            disabled={disabled}
                            onClick={() => onChange({ choice: 'candidate', candidate: c.index })}
                            sx={{ ...tile(active), ...checkerboard }}
                        >
                            {images[c.name] ? (
                                <Box component="img" src={images[c.name]} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            ) : (
                                <CircularProgress size={18} />
                            )}
                        </ButtonBase>
                    );
                })}
                {candidates.length > 0 && (
                    <ButtonBase
                        type="button"
                        aria-pressed={value.choice === 'none'}
                        disabled={disabled}
                        onClick={() => onChange({ choice: 'none' })}
                        sx={{ ...tile(value.choice === 'none'), typography: 'body2', color: 'text.secondary' }}
                    >
                        No logo
                    </ButtonBase>
                )}
                {searching && candidates.length === 0 && <CircularProgress size={20} />}
            </Box>
            <Typography variant="body2" color={value.choice === 'auto' && logo?.low_confidence ? 'warning.dark' : 'text.secondary'} sx={{ mt: 1 }}>
                {caption}
            </Typography>
        </Box>
    );
}
